// UBB-341: this module installs PAGE-LEVEL machinery — a MutationObserver and a wrapper around
// LoopSubscriptions.startLoopWidget — and nothing tears the previous ones down. Calling loopWidget()
// twice therefore leaves TWO observers watching the same root: each self-disconnects around its own
// inject(), but the other still sees those mutations, and inject() unconditionally rewrites the
// badge / compare-at textContent, so every pass produces a fresh mutation. They feed each other and
// the main thread never gets it back (froze the tab on a second quick-view open).
//
// Two callers exist now — product.autojs.js on the PDP and loop-widget-quickview.js on any page with
// a quick-view — so the guard lives HERE rather than in either caller. Re-entry still restyles what
// is currently on screen; it just doesn't install a second set of hooks.
//
// The flag lives on `window`, NOT in module scope. This file is bundled into BOTH global.autojs.js
// and product.autojs.js, and webpack gives each bundle its own copy of the module — so a module-level
// flag is two independent variables and both copies still install hooks. On a PDP both bundles load,
// which is exactly the case that kept freezing after the module-scoped attempt.
const INIT_FLAG = '__urbanBrewLoopWidgetInitialised';

function loopWidget() {
  const dataEl = document.getElementById('loop-product-data');
  if (!dataEl) return;

  let productData;
  try {
    productData = JSON.parse(dataEl.textContent);
  } catch {
    return;
  }

  // Benefits: PREFER the subscribe_benefit block's own data node (a block file reads its OWN settings
  // correctly), falling back to #loop-product-data's array — which, due to the @theme iteration bug
  // (file blocks report type '@theme' + empty settings to section-level filters), only ever carries
  // the defaults. Owner-approved fix 2026-07-05; additive — the main-product feed is untouched.
  // Resolved PER inject() CALL (not captured once) and SCOPED to the widget's own section, so:
  // (a) a quickview modal's injected MainProduct- section reads its OWN node, never the host page's;
  // (b) theme-editor section re-renders pick up fresh settings on the next Loop re-init.
  function resolveBenefits(scopeStart) {
    const section = scopeStart
      ? scopeStart.closest('section[id^="MainProduct-"]')
      : null;
    const benefitsEl =
      (section && section.querySelector('[data-loop-subscribe-benefits]')) ||
      document.querySelector('[data-loop-subscribe-benefits]');
    if (benefitsEl) {
      try {
        const blockBenefits = JSON.parse(benefitsEl.textContent).benefits;
        if (Array.isArray(blockBenefits) && blockBenefits.length > 0) {
          return blockBenefits;
        }
      } catch {
        // malformed node — fall through to the payload fallback
      }
    }
    return productData.subscribeBenefits;
  }

  // Resolves price data for the currently selected variant from #loop-variant-prices —
  // a separate script tag so that any issue with it never affects the main #loop-product-data
  // JSON (which would kill loopWidget() entirely if malformed). Scoped to the product section
  // so quickview and main PDP are isolated. Falls back to initial-variant prices if the
  // lookup fails for any reason (missing tag, bad JSON, unknown variant ID).
  function resolveVariantPrices(scopeStart) {
    const variantDataEl = document.getElementById('loop-variant-prices');
    if (variantDataEl) {
      try {
        const allVariants = JSON.parse(variantDataEl.textContent);
        const section = scopeStart
          ? scopeStart.closest('section[id^="MainProduct-"]')
          : null;
        // Prefer the CHECKED size radio: its checked state updates synchronously on the
        // user's click, while Motto writes the form's [name="id"] value on its own clock —
        // a .value change fires no DOM mutation, so a restyle pass triggered by Loop's
        // re-render can read a stale id from the form input and stamp the previous
        // variant's compare-at/badge onto the new price (seen in the quick-view modal,
        // where the widget also sits outside any MainProduct- section wrapper).
        const scopeRoot = section || document;
        const variantInput =
          scopeRoot.querySelector('.product-info__size-input:checked') ||
          scopeRoot.querySelector('input[name="id"]') ||
          document.querySelector('input[name="id"]');
        const variantId = variantInput && variantInput.value;
        if (variantId && allVariants[variantId]) return allVariants[variantId];
      } catch {
        // malformed JSON — fall through to initial-variant fallback
      }
    }
    return {
      price: productData.price,
      compareAtPrice: productData.compareAtPrice,
      compareAtPriceFormatted: productData.compareAtPriceFormatted,
    };
  }

  // Idempotent — safe to call repeatedly; only acts when elements are missing
  function inject() {
    const onetimeOption = document.querySelector(
      '.loop-widget-purchase-option-onetime'
    );
    if (!onetimeOption) return;

    const container = onetimeOption.closest(
      '.loop-widget-purchase-options-container'
    );
    if (!container) return;

    // Resolve prices for the currently selected variant per inject() call so the
    // saving % and compare-at stay accurate after variant switches.
    // A compare-at price higher than the current price = a one-time discount to surface.
    // ONLY the "Saving X%" badge + compare-at price depend on this; the subscribe styling,
    // benefits grid and custom radios must render regardless (a sub can discount with no
    // compare-at, which previously fell through to raw, unstyled Loop DOM).
    const { price, compareAtPrice, compareAtPriceFormatted } =
      resolveVariantPrices(onetimeOption);
    const hasDiscount = Boolean(compareAtPrice) && compareAtPrice > price;
    const savingPercent = hasDiscount
      ? Math.round(((compareAtPrice - price) / compareAtPrice) * 100)
      : 0;

    const compareAtEl = onetimeOption.querySelector(
      '.loop-widget-purchase-option-compare-at-price'
    );
    const badgeEl = onetimeOption.querySelector(
      '.loop-widget-purchase-option-discount-badge'
    );
    const priceContainer = onetimeOption.querySelector(
      '.loop-widget-purchase-option-price-container'
    );

    if (hasDiscount) {
      // Always update text — variant switch may have changed the compare-at price
      if (compareAtEl) {
        compareAtEl.textContent = compareAtPriceFormatted;
        compareAtEl.classList.remove('loop-display-none');
      }

      if (badgeEl && priceContainer) {
        // Always update text — variant switch may have changed the saving %
        badgeEl.textContent = `Saving ${savingPercent}%`;
        badgeEl.classList.remove('loop-display-none');
        // Only move if not already in the price container (DOM move is not idempotent)
        if (!priceContainer.contains(badgeEl)) {
          priceContainer.appendChild(badgeEl);
        }
      }
    } else {
      // This variant has no one-time discount — keep both elements hidden.
      // Guards against Loop partially re-rendering without resetting these.
      if (compareAtEl) compareAtEl.classList.add('loop-display-none');
      if (badgeEl) badgeEl.classList.add('loop-display-none');
    }

    // Move the subscribe badge into its price row
    // (Loop places it in the label row by default, same as one-time)
    const subscribeOption = container.querySelector(
      '.loop-widget-purchase-option:not(.loop-widget-purchase-option-onetime)'
    );
    if (subscribeOption) {
      const subscribeBadge = subscribeOption.querySelector(
        '.loop-widget-purchase-option-discount-badge'
      );
      const subscribePriceContainer = subscribeOption.querySelector(
        '.loop-widget-purchase-option-price-container'
      );
      if (
        subscribeBadge &&
        subscribePriceContainer &&
        !subscribePriceContainer.contains(subscribeBadge)
      ) {
        subscribePriceContainer.appendChild(subscribeBadge);
      }

      // UBB-268: When the product is on sale, Loop's subscribe compare-at shows the
      // sale price as the base, and the badge only reflects the subscription discount %.
      // Override both so the compare-at shows the original price and the badge reflects
      // the combined saving from original → actual subscribe price.
      // The actual subscribe price charged by Loop is left untouched.
      if (hasDiscount) {
        const subscribeCompareAtEl = subscribeOption.querySelector(
          '.loop-widget-purchase-option-compare-at-price'
        );

        // Read Loop's original discount % from a cached data attribute, falling back to
        // parsing the badge text the first time. The attribute is set once from Loop's
        // original text and preserved across inject() calls on the same element — this
        // prevents a cascading compounding error where we re-parse our own overwritten
        // badge text ("Save 32% TOTAL") and treat it as Loop's discount on the next call.
        let loopDiscountFraction = null;
        if (subscribeBadge) {
          if (subscribeBadge.dataset.loopOriginalDiscount !== undefined) {
            loopDiscountFraction = parseFloat(
              subscribeBadge.dataset.loopOriginalDiscount
            );
          } else {
            const percentMatch =
              subscribeBadge.textContent.match(/(\d+(?:\.\d+)?)\s*%/);
            if (percentMatch) {
              loopDiscountFraction = parseFloat(percentMatch[1]) / 100;
              subscribeBadge.dataset.loopOriginalDiscount =
                String(loopDiscountFraction);
            }
          }
        }

        if (loopDiscountFraction !== null) {
          // Show the original Shopify price as the subscribe compare-at.
          if (subscribeCompareAtEl) {
            subscribeCompareAtEl.textContent = compareAtPriceFormatted;
            subscribeCompareAtEl.classList.remove('loop-display-none');
          }

          // Recalculate badge % as the total saving from original price → actual subscribe price.
          // Loop charges sale price × (1 - discount), so total saving vs original is larger.
          const loopSubscribePriceCents = Math.round(
            price * (1 - loopDiscountFraction)
          );
          const totalSavingPercent = Math.round(
            ((compareAtPrice - loopSubscribePriceCents) / compareAtPrice) * 100
          );
          if (subscribeBadge) {
            subscribeBadge.textContent = `Save ${totalSavingPercent}% TOTAL`;
          }
        }
      }

      // Inject benefits grid below the subscribe header (idempotent). Built with
      // createElement/textContent — NOT innerHTML — because merchant-entered benefit text
      // now actually flows here (the @theme-bug fix); interpolating it into markup would
      // be an XSS/rendering sink ("Pause < resume" would swallow the span).
      const subscribeBenefits = resolveBenefits(onetimeOption);
      if (
        !subscribeOption.querySelector('.loop-subscribe-benefits') &&
        subscribeBenefits &&
        subscribeBenefits.length > 0
      ) {
        const benefits = document.createElement('div');
        benefits.className = 'loop-subscribe-benefits';
        subscribeBenefits.forEach((benefit) => {
          const row = document.createElement('div');
          row.className = 'loop-subscribe-benefit';
          const icon = document.createElement('img');
          icon.className = 'loop-subscribe-benefit-icon';
          icon.alt = '';
          icon.src = benefit.icon || '';
          const text = document.createElement('span');
          text.className = 'loop-subscribe-benefit-text';
          text.textContent = benefit.text || '';
          row.appendChild(icon);
          row.appendChild(text);
          benefits.appendChild(row);
        });
        subscribeOption.appendChild(benefits);
      }
    }

    // UBB-341: an option with nothing to save must not render a saving line at all. Loop still
    // emits the pill (empty, or "Save 0%") when a selling plan carries no discount, which reads as
    // a broken chip. Hide the pill AND the struck-through compare-at, which is meaningless without
    // a saving. Runs for BOTH options, after all the text rewriting above.
    //
    // Test is "contains a non-zero digit", NOT a %-match: Loop can express a saving as a fixed
    // amount ("Save $5"), and a %-only test would wrongly hide that. Empty / "Save 0%" / "0.00"
    // all fall through to hidden.
    [
      onetimeOption,
      container.querySelector(
        '.loop-widget-purchase-option:not(.loop-widget-purchase-option-onetime)'
      ),
    ].forEach((option) => {
      if (!option) return;
      const badge = option.querySelector(
        '.loop-widget-purchase-option-discount-badge'
      );
      if (!badge) return;
      if (/[1-9]/.test(badge.textContent)) return; // a real saving — leave it alone

      badge.classList.add('loop-display-none');
      const compareAt = option.querySelector(
        '.loop-widget-purchase-option-compare-at-price'
      );
      if (compareAt) compareAt.classList.add('loop-display-none');
    });

    // Inject check circle into every radio element (both options)
    container
      .querySelectorAll('.loop-widget-purchase-option-radio')
      .forEach((radio) => {
        if (!radio.querySelector('.loop-custom-check-circle')) {
          const checkCircle = document.createElement('span');
          checkCircle.className = 'loop-custom-check-circle';
          radio.appendChild(checkCircle);
        }
      });
  }

  // Already hooked by an earlier call — the observer below is page-wide and keeps restyling every
  // later render, so just style whatever is on screen right now and leave the hooks alone.
  if (window[INIT_FLAG]) {
    inject();
    return;
  }
  window[INIT_FLAG] = true;

  // Primary: intercept LoopSubscriptions.startLoopWidget so inject() fires after
  // the widget DOM is ready. This works when our script loads before Loop calls it.
  if (
    window.LoopSubscriptions &&
    typeof window.LoopSubscriptions.startLoopWidget === 'function'
  ) {
    const originalStart = window.LoopSubscriptions.startLoopWidget;
    window.LoopSubscriptions.startLoopWidget = async function (productId) {
      const result = await originalStart.call(this, productId);
      inject();
      return result;
    };
  }

  // Fallback: if Loop already ran startLoopWidget before our script loaded,
  // attempt an immediate inject (idempotent — no-ops if elements aren't ready yet)
  inject();

  // Robustness layer: product.autojs.js loads `defer`, so our script runs AFTER
  // Loop has defined/called startLoopWidget, and Loop redefines it afterward —
  // overwriting the hook above so it doesn't stick. A persistent observer re-runs
  // the idempotent inject() on every relevant DOM change, so styling survives that
  // late redefinition AND re-applies on later Loop renders / variant changes.
  // Disconnect before injecting to prevent our own DOM mutations from retriggering
  // the callback, then reconnect immediately after. Scope the observed root to the
  // product section (falling back to body) to keep the watched subtree small.
  const observerRoot =
    document.querySelector('section[id^="MainProduct-"]') || document.body;
  const observer = new MutationObserver(() => {
    observer.disconnect();
    inject();
    observer.observe(observerRoot, { childList: true, subtree: true });
  });
  observer.observe(observerRoot, { childList: true, subtree: true });
}

export default loopWidget;
